import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { AiOutlineClose } from "react-icons/ai";
import { FaDownload, FaEye, FaEyeSlash, FaHistory, FaKey, FaSignOutAlt, FaTrash, FaUser } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { logoutUser, resetPassword } from "../Redux/Slice/auth.slice";
import { changePassword } from "../Redux/Slice/user.slice";

// Sidebar items
const sidebarItems = [
  {
    id: "my-profile", label: "My Profile",
    icon: <FaUser />,
    path: ""
  },
  {
    id: "orders",
    label: "Order History",
    icon: <FaHistory />,
    path: "orders",
  },
  {
    id: "downloads",
    label: "My Downloads",
    icon: <FaDownload />,
    path: "downloads",
  },
  {
    id: "password",
    label: "Change Password",
    icon: <FaKey />,
    path: "password",
  },
  {
    id: "delete",
    label: "Delete Account",
    icon: <FaTrash />,
    path: "delete",
  },
  {
    id: "logout", label: "Logout",
    icon: <FaSignOutAlt />,
    path: "logout"
  },
];

// Sidebar for large screens
export const Sidebar1 = ({ activeItem }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currUser);
  const userId = localStorage.getItem('yoyouserId');
  //   LogOut code :
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  //   Change Password code :
  const [showChangePass, setShowChangePass] = useState(false);
  const CancelPassChange = () => {
    setShowChangePass(false);
  };

  useEffect(() => {
    if (showLogoutModal || showChangePass) {

      const scrollY = window.scrollY;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {

      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';


      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {

      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showLogoutModal || showChangePass]);

  const handleSidebarClick = (id, path) => {
    if (id === "logout") {
      setShowLogoutModal(true);
    } else if (id === "password") {
      setShowChangePass(true);
    } else {
      navigate(`/profile/${path}`);
    }
  };

  const handleLogout = async () => {
    if (userId) {
      await dispatch(logoutUser(userId));
    }
    localStorage.removeItem("yoyouserId");
    localStorage.removeItem("yoyoToken");
    localStorage.removeItem("role");
    navigate("/")
  }

  return (
    <>
      <aside className="w-full md:w-[280px] sticky top-32 hidden md:block bg-[#1a1a1a]/60 backdrop-blur-xl border border-white/10 rounded-[32px] sm:rounded-[40px] overflow-hidden mb-6 md:mb-0 md:mr-4 lg:mr-8 p-3 shadow-2xl cursor-pointer">
        <div className="flex flex-col gap-1.5">
          {sidebarItems.map(({ id, label, icon, path }) => {
            const isActive = activeItem === id || (id === "my-profile" && activeItem === "edit-profile");
            return (
              <div
                key={id}
                onClick={() => handleSidebarClick(id, path)}
                className={`group flex items-center gap-4 px-6 py-4 rounded-2xl cursor-pointer transition-all duration-300 ${isActive
                    ? "bg-white/10 text-white border-l-4 border-[#6263ED] shadow-inner"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`}
              >
                <div className={`text-xl transition-colors duration-300 ${isActive ? "text-[#6263ED]" : "opacity-50 group-hover:opacity-100"}`}>
                  {icon}
                </div>
                <span className={`font-bold text-sm tracking-wide transition-all duration-300 ${isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"}`}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-[#1a1a1a]/90 backdrop-blur-xl border border-white/10 p-6 sm:p-8 text-white w-full max-w-[400px] rounded-[32px] shadow-2xl relative overflow-hidden"
          >
            {/* Decorative background glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#6263ED]/20 rounded-full blur-[80px]" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#A7006F]/20 rounded-full blur-[80px]" />

            <div className="flex justify-between items-center mb-6 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg shadow-[#6263ED]/20">
                  <FaSignOutAlt className="text-white text-lg" />
                </div>
                <h1 className="text-xl font-bold tracking-tight">Ready to leave?</h1>
              </div>
              <button
                onClick={cancelLogout}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
              >
                <AiOutlineClose className="text-xl" />
              </button>
            </div>

            <p className="text-gray-400 text-sm leading-relaxed mb-8 relative z-10 text-center sm:text-left">
              Are you sure you want to log out? You'll need to sign back in to access your purchased games and profile.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 relative z-10">
              <button
                onClick={cancelLogout}
                className="flex-1 order-2 sm:order-1 px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all duration-300"
              >
                Stay Logged In
              </button>
              <button
                onClick={handleLogout}
                className="flex-[1.2] order-1 sm:order-2 px-6 py-4 rounded-2xl bg-gradient-primary text-white font-bold text-sm shadow-lg shadow-[#6263ED]/20 hover:shadow-[#6263ED]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
              >
                Yes, Logout
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Change Password form */}
      {showChangePass && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-[#1a1a1a]/90 backdrop-blur-xl border border-white/10 p-6 sm:p-8 text-white w-full max-w-[450px] rounded-[32px] shadow-2xl relative overflow-hidden"
          >
            {/* Decorative background glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#6263ED]/20 rounded-full blur-[80px]" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#A7006F]/20 rounded-full blur-[80px]" />

            <div className="flex justify-between items-center mb-8 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg shadow-[#6263ED]/20">
                  <FaKey className="text-white text-lg" />
                </div>
                <h1 className="text-xl font-bold tracking-tight">Security Update</h1>
              </div>
              <button
                onClick={CancelPassChange}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
              >
                <AiOutlineClose className="text-xl" />
              </button>
            </div>

            <Formik
              initialValues={{ oldPassword: '', newPassword: '', confirmNewPassword: '', showOldPassword: false, showNewPassword: false, showConfirmNewPassword: false }}
              validate={values => {
                const errors = {};
                if (!values.oldPassword) errors.oldPassword = 'Required';
                if (!values.newPassword) {
                  errors.newPassword = 'Required';
                } else if (values.newPassword.length < 6) {
                  errors.newPassword = 'Minimum 6 characters';
                } else if (values.newPassword !== values.confirmNewPassword) {
                  errors.confirmNewPassword = 'Passwords do not match';
                }
                return errors;
              }}
              onSubmit={(values) => {
                const { oldPassword, newPassword } = values;
                dispatch(changePassword({ email: currentUser.email, oldPassword, newPassword })).then((response) => {
                  if (response.payload.success) setShowChangePass(false);
                });
              }}
            >
              {({ handleChange, handleSubmit, values, errors, touched, setFieldValue }) => (
                <form onSubmit={handleSubmit} className="relative z-10 flex flex-col gap-5">
                  {[
                    { id: 'oldPassword', label: 'Current Password', showId: 'showOldPassword', placeholder: 'Enter current password' },
                    { id: 'newPassword', label: 'New Password', showId: 'showNewPassword', placeholder: 'Create new password' },
                    { id: 'confirmNewPassword', label: 'Confirm New Password', showId: 'showConfirmNewPassword', placeholder: 'Re-type new password' }
                  ].map((field) => (
                    <div key={field.id} className="space-y-2">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">{field.label}</label>
                      <div className="relative group">
                        <input
                          type={values[field.showId] ? "text" : "password"}
                          placeholder={field.placeholder}
                          name={field.id}
                          className={`w-full bg-white/5 border ${errors[field.id] && touched[field.id] ? 'border-red-500/50' : 'border-white/10'} focus:border-[#6263ED]/50 focus:bg-white/[0.08] text-sm text-white px-5 py-4 rounded-2xl outline-none transition-all duration-300 group-hover:border-white/20`}
                          value={values[field.id]}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                          onClick={() => setFieldValue(field.showId, !values[field.showId])}
                        >
                          {values[field.showId] ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      {errors[field.id] && touched[field.id] && (
                        <motion.p initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="text-red-400 text-[10px] font-bold uppercase tracking-wider ml-1">
                          {errors[field.id]}
                        </motion.p>
                      )}
                    </div>
                  ))}

                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button
                      type="button"
                      onClick={CancelPassChange}
                      className="flex-1 order-2 sm:order-1 px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all duration-300"
                    >
                      Maybe Later
                    </button>
                    <button
                      type="submit"
                      className="flex-[1.5] order-1 sm:order-2 px-6 py-4 rounded-2xl bg-gradient-primary text-white font-bold text-sm shadow-lg shadow-[#6263ED]/20 hover:shadow-[#6263ED]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

// Sidebar for mobile
export const Sidebar2 = ({ activeItem }) => {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currUser);
  const userId = localStorage.getItem('yoyouserId');
  const oldPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmNewPasswordRef = useRef(null);

  // log Out code:
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  //   Change Password code :
  const [showChangePass, setShowChangePass] = useState(false);
  const CancelPassChange = () => {
    setShowChangePass(false);
  };

  useEffect(() => {
    if (showLogoutModal || showChangePass) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showLogoutModal || showChangePass]);

  const handleSidebarClick = (id, path) => {
    if (id === "logout") {
      setShowLogoutModal(true);
    } else if (id === "password") {
      setShowChangePass(true);
    } else {
      navigate(`/profile/${path}`);
    }
  };

  const handleLogout = async () => {
    if (userId) {
      await dispatch(logoutUser(userId));
    }
    localStorage.removeItem("yoyouserId");
    localStorage.removeItem("yoyoToken");
    localStorage.removeItem("role");
    navigate("/")
  }

  // --- Scroll Button State ---
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position to enable/disable buttons
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  // Attach scroll event
  React.useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  // Scroll handlers
  const scrollByAmount = 120; // px
  const handleScrollLeft = () => {
    scrollRef.current && scrollRef.current.scrollBy({ left: -scrollByAmount, behavior: 'smooth' });
  };
  const handleScrollRight = () => {
    scrollRef.current && scrollRef.current.scrollBy({ left: scrollByAmount, behavior: 'smooth' });
  };

  return (
    <>
      <aside className="relative w-full mb-8 md:hidden">
        <div className="relative bg-[#1a1a1a]/60 backdrop-blur-xl border border-white/10 rounded-[28px] sm:rounded-[32px] overflow-hidden mx-auto w-full shadow-xl">
          <div
            ref={scrollRef}
            className="flex flex-row overflow-x-auto scrollbar-hide p-2.5 gap-2.5 scroll-smooth"
          >
            {sidebarItems.map(({ id, label, icon, path }) => {
              const isActive = activeItem === id || (id === "my-profile" && activeItem === "edit-profile");
              return (
                <button
                  key={id}
                  onClick={() => handleSidebarClick(id, path)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-[13px] font-bold tracking-wide transition-all duration-300 whitespace-nowrap flex-shrink-0 ${isActive
                      ? "bg-gradient-primary text-white shadow-lg shadow-[#6263ED]/30 scale-[1.02]"
                      : "bg-white/5 text-gray-400 hover:text-white"
                    }`}
                >
                  <span className={isActive ? "text-white" : "opacity-60"}>{icon}</span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal (Mobile) */}
      {showLogoutModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-[#1a1a1a]/90 backdrop-blur-xl border border-white/10 p-6 sm:p-8 text-white w-full max-w-[400px] rounded-[32px] shadow-2xl relative overflow-hidden"
          >
            {/* Decorative background glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#6263ED]/20 rounded-full blur-[80px]" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#A7006F]/20 rounded-full blur-[80px]" />

            <div className="flex justify-between items-center mb-6 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg shadow-[#6263ED]/20">
                  <FaSignOutAlt className="text-white text-lg" />
                </div>
                <h1 className="text-xl font-bold tracking-tight">Ready to leave?</h1>
              </div>
              <button
                onClick={cancelLogout}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
              >
                <AiOutlineClose className="text-xl" />
              </button>
            </div>

            <p className="text-gray-400 text-sm leading-relaxed mb-8 relative z-10 text-center sm:text-left">
              Are you sure you want to log out? You'll need to sign back in to access your purchased games and profile.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 relative z-10">
              <button
                onClick={cancelLogout}
                className="flex-1 order-2 sm:order-1 px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all duration-300"
              >
                Stay Logged In
              </button>
              <button
                onClick={handleLogout}
                className="flex-[1.2] order-1 sm:order-2 px-6 py-4 rounded-2xl bg-gradient-primary text-white font-bold text-sm shadow-lg shadow-[#6263ED]/20 hover:shadow-[#6263ED]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
              >
                Yes, Logout
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Change Password form (Mobile) */}
      {showChangePass && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-[#1a1a1a]/90 backdrop-blur-xl border border-white/10 p-6 sm:p-8 text-white w-full max-w-[450px] rounded-[32px] shadow-2xl relative overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#6263ED]/20 rounded-full blur-[80px]" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#A7006F]/20 rounded-full blur-[80px]" />

            <div className="flex justify-between items-center mb-8 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg shadow-[#6263ED]/20">
                  <FaKey className="text-white text-lg" />
                </div>
                <h1 className="text-xl font-bold tracking-tight">Security Update</h1>
              </div>
              <button
                onClick={CancelPassChange}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
              >
                <AiOutlineClose className="text-xl" />
              </button>
            </div>

            <Formik
              initialValues={{ oldPassword: '', newPassword: '', confirmNewPassword: '', showOldPassword: false, showNewPassword: false, showConfirmNewPassword: false }}
              validate={values => {
                const errors = {};
                if (!values.oldPassword) errors.oldPassword = 'Required';
                if (!values.newPassword) {
                  errors.newPassword = 'Required';
                } else if (values.newPassword.length < 6) {
                  errors.newPassword = 'Minimum 6 characters';
                } else if (values.newPassword !== values.confirmNewPassword) {
                  errors.confirmNewPassword = 'Passwords do not match';
                }
                return errors;
              }}
              onSubmit={(values) => {
                const { oldPassword, newPassword } = values;
                dispatch(resetPassword({ email: currentUser.email, oldPassword, newPassword })).then((response) => {
                  if (response.payload.success) setShowChangePass(false);
                });
              }}
            >
              {({ handleChange, handleSubmit, values, errors, touched, setFieldValue }) => (
                <form onSubmit={handleSubmit} className="relative z-10 flex flex-col gap-5">
                  {[
                    { id: 'oldPassword', label: 'Current Password', showId: 'showOldPassword', placeholder: 'Enter current password', ref: oldPasswordRef },
                    { id: 'newPassword', label: 'New Password', showId: 'showNewPassword', placeholder: 'Create new password', ref: newPasswordRef },
                    { id: 'confirmNewPassword', label: 'Confirm New Password', showId: 'showConfirmNewPassword', placeholder: 'Re-type new password', ref: confirmNewPasswordRef }
                  ].map((field) => (
                    <div key={field.id} className="space-y-2">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">{field.label}</label>
                      <div className="relative group">
                        <input
                          type={values[field.showId] ? "text" : "password"}
                          placeholder={field.placeholder}
                          name={field.id}
                          ref={field.ref}
                          className={`w-full bg-white/5 border ${errors[field.id] && touched[field.id] ? 'border-red-500/50' : 'border-white/10'} focus:border-[#6263ED]/50 focus:bg-white/[0.08] text-sm text-white px-5 py-4 rounded-2xl outline-none transition-all duration-300 group-hover:border-white/20`}
                          value={values[field.id]}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                          onClick={() => setFieldValue(field.showId, !values[field.showId])}
                        >
                          {values[field.showId] ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      {errors[field.id] && touched[field.id] && (
                        <motion.p initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="text-red-400 text-[10px] font-bold uppercase tracking-wider ml-1">
                          {errors[field.id]}
                        </motion.p>
                      )}
                    </div>
                  ))}

                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button
                      type="button"
                      onClick={CancelPassChange}
                      className="flex-1 order-2 sm:order-1 px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all duration-300"
                    >
                      Maybe Later
                    </button>
                    <button
                      type="submit"
                      className="flex-[1.5] order-1 sm:order-2 px-6 py-4 rounded-2xl bg-gradient-primary text-white font-bold text-sm shadow-lg shadow-[#6263ED]/20 hover:shadow-[#6263ED]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};
